/**
 * AI Movies & Series — Route: /ai-movies-series
 * 7-step production path: Idea & Format → Render Style → Screenplay → Cast & Voices → Storyboard & Shots → Sound → Final Cut.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, FolderOpen, Hand, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  TIER_RES, createProject, creditPreview, deleteProject, errMsg, getCatalog, listProjects, listVoices, patchProject, cancelJob,
} from '../lib/aiStudioApi';
import { useMovieProject } from '../components/movies/useMovieProject';
import { CreateMovieDialog } from '../components/movies/CreateMovieDialog';
import { AutoDirectorPanel } from '../components/movies/AutoDirectorPanel';
import { ScreenplayView } from '../components/movies/ScreenplayView';
import { ShotForm } from '../components/movies/ShotForm';
import { SceneCard } from '../components/movies/SceneCard';
import { ShowKitPanel } from '../components/movies/ShowKitPanel';
import { VoiceLibrary } from '../components/movies/VoiceLibrary';
import { ProductionBar } from '../components/movies/ProductionBar';
import { ScreeningRoom } from '../components/movies/ScreeningRoom';
import { StudioStepper, StepNav, STUDIO_STEPS, firstOpenStep, stepStatus } from '../components/movies/StudioStepper';
import { RenderStyleGallery } from '../components/movies/RenderStyleGallery';
import { SoundStep } from '../components/movies/SoundStep';
import { FinalCutPanel } from '../components/movies/FinalCutPanel';
import { ProjectTitle } from '../components/movies/ProjectTitle';
import { cn } from '../lib/utils';

function ModeSwitch({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-neutral-800 bg-neutral-950 p-0.5" data-testid="director-mode-switch">
      {[['auto', 'AI-Directed', Sparkles], ['manual', 'Manual', Hand]].map(([v, label, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`director-mode-${v}`}
          className={cn('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
            mode === v ? 'bg-amber-500 text-neutral-900' : 'text-neutral-400 hover:text-neutral-200')}>
          <Icon size={13} /> {label}
        </button>
      ))}
    </div>
  );
}

function IdeaStep({ project, onPatch }) {
  const rows = [
    ['Format', project.format === 'series' ? `Series · Season ${project.season || 1} · ${project.episodes_count || 1} episodes` : 'Feature film'],
    ['Aspect ratio', project.aspect_ratio], ['Language', project.language || 'English'], ['Tone', project.tone || '—'],
  ];
  return (
    <div className="grid gap-4 md:grid-cols-[1fr_1fr]" data-testid="idea-step">
      <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-5">
        <div className="mb-3 text-sm font-semibold text-neutral-100">Production basics</div>
        {rows.map(([k, v]) => <div key={k} className="flex justify-between border-b border-neutral-900 py-2 text-xs last:border-0"><span className="text-neutral-500">{k}</span><span className="text-neutral-200">{v}</span></div>)}
        {project.adaptation?.source_title && <Badge className="mt-3 bg-amber-500/15 text-amber-200">Adaptation · {project.adaptation.source_title} · {project.adaptation.culture_label}</Badge>}
      </div>
      <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-5">
        <div className="mb-1 text-sm font-semibold text-neutral-100">Who directs?</div>
        <p className="mb-4 text-xs text-neutral-500">AI-Directed turns one idea into a full screenplay, cast and shot list. Manual lets you design every shot yourself. You can switch any time.</p>
        <ModeSwitch mode={project.mode || 'manual'} onChange={(mode) => onPatch({ mode })} />
      </div>
    </div>
  );
}

export default function AIMoviesSeriesPage() {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [voices, setVoices] = useState({ stock: [], custom: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep] = useState(1);
  const [epFilter, setEpFilter] = useState(0);
  const [costs, setCosts] = useState({});
  const { project, reload } = useMovieProject(activeId);
  const resolution = TIER_RES[project?.quality_tier || 'standard'] || '1080p';

  const refreshProjects = useCallback(async () => {
    const list = await listProjects('movie');
    setProjects(list);
    setActiveId((id) => id || list[0]?.id || null);
  }, []);
  const refreshVoices = useCallback(() => listVoices().then(setVoices).catch(() => {}), []);
  useEffect(() => {
    refreshProjects().catch((e) => toast.error(errMsg(e)));
    getCatalog().then(setCatalog).catch((e) => toast.error(errMsg(e)));
    refreshVoices();
  }, [refreshProjects, refreshVoices]);

  const styleLabel = catalog?.render_styles?.find((s) => s.id === project?.render_style)?.label;
  const status = useMemo(() => (project ? stepStatus(project, styleLabel) : null), [project, styleLabel]);
  useEffect(() => { if (status) setStep(firstOpenStep(status)); }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const durations = useMemo(() => [...new Set((project?.scenes || []).map((s) => s.duration_seconds))].join(','), [project?.scenes]);
  useEffect(() => {
    durations.split(',').filter(Boolean).forEach((d) => {
      const key = `${d}:${resolution}`;
      if (costs[key] === undefined) creditPreview(Number(d), resolution).then((r) => setCosts((c) => ({ ...c, [key]: r.credits }))).catch(() => {});
    });
  }, [durations, resolution]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate(payload) {
    try {
      const p = await createProject(payload);
      setShowCreate(false); await refreshProjects(); setActiveId(p.id);
      toast.success('Production created');
    } catch (e) { toast.error(errMsg(e)); }
  }
  const update = async (patch) => { try { await patchProject(project.id, patch); await reload(); } catch (e) { toast.error(errMsg(e)); } };
  const rename = async (title) => { await update({ title }); await refreshProjects(); toast.success('Production renamed'); };
  async function handleDelete() {
    if (!window.confirm(`Delete "${project.title}"? This cannot be undone.`)) return;
    await deleteProject(project.id); setActiveId(null); await refreshProjects();
  }
  const go = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const scenes = project?.scenes || [];
  const eps = project?.format === 'series' ? project.episodes_count || 1 : 1;
  const shown = epFilter ? scenes.filter((s) => (s.episode || 1) === epFilter) : scenes;

  const stepBody = project && catalog && {
    1: <IdeaStep project={project} onPatch={update} />,
    2: <RenderStyleGallery project={project} catalog={catalog} onPatch={update} />,
    3: (
      <div className="space-y-5">
        {project.mode === 'auto'
          ? <AutoDirectorPanel key={project.id} project={project} catalog={catalog} onStarted={reload} />
          : <ShotForm project={project} catalog={catalog} voices={voices} onAdded={reload} />}
        <ScreenplayView project={project} onCancel={() => cancelJob(project.id, 'direction').then(reload)} />
      </div>
    ),
    4: (
      <div className="space-y-8">
        <ProductionBar project={project} resolution={resolution} onChanged={reload} />
        <ShowKitPanel project={project} voices={voices} onChanged={reload} />
        <VoiceLibrary project={project} voices={voices} onVoicesChanged={refreshVoices} onNarrator={(v) => update({ narrator_voice: v })} />
      </div>
    ),
    5: (
      <div className="space-y-4">
        <ProductionBar project={project} resolution={resolution} onChanged={reload} />
        {eps > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {[0, ...Array.from({ length: eps }, (_, i) => i + 1)].map((n) => (
              <button key={n} type="button" onClick={() => setEpFilter(n)} data-testid={`shots-filter-ep-${n}`}
                className={cn('rounded-md px-2.5 py-1 text-xs', epFilter === n ? 'bg-amber-500 text-neutral-900' : 'bg-neutral-900 text-neutral-400')}>
                {n ? `EP${n}` : 'All'}
              </button>
            ))}
          </div>
        )}
        {shown.length === 0 && <div className="rounded-xl border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-500">No shots yet — write the screenplay in step 3.</div>}
        {shown.map((s) => (
          <SceneCard key={s.id} project={project} scene={s} index={scenes.filter((x) => (x.episode || 1) === (s.episode || 1)).indexOf(s)}
            catalog={catalog} voices={voices} resolution={resolution} previewCredits={costs[`${s.duration_seconds}:${resolution}`]} onChanged={reload} />
        ))}
      </div>
    ),
    6: <SoundStep project={project} catalog={catalog} onPatch={update} onChanged={reload} />,
    7: (
      <div className="space-y-8">
        <FinalCutPanel project={project} onPatch={update} onChanged={reload} onGoToStep={go} />
        <div>
          <div className="mb-3 text-xs uppercase tracking-[0.2em] text-neutral-500">Screening Room · live preview of finished shots</div>
          <ScreeningRoom project={project} onChanged={reload} />
        </div>
      </div>
    ),
  };

  return (
    <div className="studio-dark min-h-screen bg-neutral-950 text-neutral-100" data-testid="ai-movies-series-page">
      <div className="mx-auto max-w-[1440px] px-6 py-10">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-300"><Clapperboard size={14} /> Konnect Media Core · Studio</div>
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">AI Movies & Series</h1>
            <p className="mt-2 max-w-2xl text-sm text-neutral-400">
              Seven clear steps from one idea to a finished film or season — pick a look, write, cast, shoot, voice and cut. Every paid step shows its credits first; the Final Cut is free.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="border-neutral-700 text-neutral-200"><Link to="/studio-library" data-testid="ai-movies-library-link"><FolderOpen size={15} className="mr-1" /> Studio Library</Link></Button>
            <Button onClick={() => setShowCreate(true)} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="ai-movies-create-btn">
              <Plus size={16} className="mr-1" /> New production
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[250px_1fr]">
          <aside className="space-y-2" data-testid="ai-movies-project-list">
            {projects.length === 0 && <Card className="border-neutral-800 bg-neutral-900/40 text-neutral-100"><CardContent className="p-4 text-sm text-neutral-400">No productions yet. Hit <strong>New production</strong>.</CardContent></Card>}
            {projects.map((p) => (
              <button key={p.id} onClick={() => { setActiveId(p.id); setEpFilter(0); }} data-testid={`ai-movies-project-${p.id}`}
                className={cn('w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  activeId === p.id ? 'border-amber-400/60 bg-amber-500/10' : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-600')}>
                <div className="truncate font-medium">{p.screenplay?.title || p.title}</div>
                <div className="text-xs text-neutral-500">{p.format === 'series' ? `Series · ${p.episodes_count} eps` : 'Film'} · {p.aspect_ratio} · {p.mode === 'auto' ? 'AI-Directed' : 'Manual'}</div>
              </button>
            ))}
          </aside>

          {!project || !catalog ? (
            <Card className="border-neutral-800 bg-neutral-900/40 text-neutral-100"><CardContent className="p-10 text-center text-sm text-neutral-500">{activeId ? 'Loading production…' : 'Pick a production — or create one.'}</CardContent></Card>
          ) : (
            <div className="min-w-0 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4">
                <div className="min-w-0">
                  <ProjectTitle title={project.screenplay?.title || project.title} onRename={rename} />
                  <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                    <Badge className="bg-neutral-800 text-neutral-300">{project.format === 'series' ? `Series · ${eps} episodes` : 'Film'}</Badge>
                    <Badge className="bg-neutral-800 text-neutral-300">{project.aspect_ratio}</Badge>
                    <Badge className="bg-neutral-800 text-neutral-300" data-testid="ai-movies-tier-badge">{resolution}</Badge>
                    {styleLabel && <Badge className="bg-amber-500/15 text-amber-200" data-testid="ai-movies-style-badge">Style lock · {styleLabel}</Badge>}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleDelete} className="border-neutral-800 text-neutral-500 hover:text-red-300" data-testid="ai-movies-delete-project"><Trash2 size={14} /></Button>
              </div>

              <StudioStepper status={status} current={step} onSelect={go} />
              <div className="space-y-5" data-testid={`studio-step-panel-${step}`}>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs text-amber-300">STEP {step} / 7</span>
                  <h2 className="text-base font-semibold text-neutral-100 md:text-lg">{STUDIO_STEPS[step - 1].label}</h2>
                </div>
                {stepBody[step]}
                <StepNav current={step} onSelect={go} />
              </div>
            </div>
          )}
        </div>
      </div>
      {catalog && <CreateMovieDialog open={showCreate} onOpenChange={setShowCreate} onCreate={handleCreate} tones={catalog.tones} />}
    </div>
  );
}
